import { Plus, FileText, Library, ChevronsLeft, ChevronsRight, ChevronsUpDown, UserPlus, Settings, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'
import { UsageMeter } from '@/components/billing/UsageMeter'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Workspace } from '@/data/mock'
import type { AuthUser } from '@/lib/auth'

export type ActiveNav = 'documents' | 'library' | null

interface SidebarProps {
  workspaces: Workspace[]
  currentWorkspaceId: string
  onWorkspaceChange: (id: string) => void
  activeNav: ActiveNav
  onNavChange: (nav: ActiveNav) => void
  collapsed: boolean
  onToggleCollapse: () => void
  onOpenSettings: () => void
  onOpenInvite: () => void
  onCreateWorkspace?: (name: string) => void
  onOpenWorkspaceSettings?: () => void
  onLogout?: () => void
  onNewDocument?: () => void
  activeWorkspaceId?: string | null
  hasLibrarySources?: boolean
  user?: AuthUser | null
}

function userInitials(user: AuthUser | null | undefined): string {
  if (!user) return '?'
  const first = (user.first_name ?? '').trim()
  const last = (user.last_name ?? '').trim()
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?'
  }
  const email = (user.email ?? '').trim()
  return email ? email.charAt(0).toUpperCase() : '?'
}

function userShortName(user: AuthUser | null | undefined): string {
  if (!user) return 'Signed in'
  const first = (user.first_name ?? '').trim()
  const last = (user.last_name ?? '').trim()
  if (first && last) return `${first} ${last.charAt(0)}.`
  if (first) return first
  return (user.email ?? '').split('@')[0] || 'Signed in'
}

export function Sidebar({
  workspaces,
  currentWorkspaceId,
  onWorkspaceChange,
  activeNav,
  onNavChange,
  collapsed,
  onToggleCollapse,
  onOpenSettings,
  onOpenInvite,
  onCreateWorkspace,
  onOpenWorkspaceSettings,
  onLogout,
  onNewDocument,
  activeWorkspaceId,
  hasLibrarySources = true,
  user = null,
}: SidebarProps) {
  const newButtonDisabled = !activeWorkspaceId || !hasLibrarySources
  const newButtonTooltip = !activeWorkspaceId
    ? 'Please select or create a workspace first'
    : 'Upload a source to the library first'
  const initials = userInitials(user)
  const shortName = userShortName(user)
  const email = user?.email ?? ''
  if (collapsed) {
    return (
      <aside className="w-14 shrink-0 bg-sidebar border-r border-border flex flex-col h-full items-center py-3 gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground mb-1"
          onClick={onToggleCollapse}
        >
          <ChevronsRight className="h-4 w-4" strokeWidth={1.5} />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="mb-2 rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs bg-pharma-primary text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-56">
            <DropdownMenuLabel className="font-normal text-xs text-muted-foreground truncate">
              {email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onOpenInvite}>
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Members
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onOpenSettings}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={onLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => onNavChange(activeNav === 'documents' ? null : 'documents')}
        >
          <FileText className="h-4 w-4" strokeWidth={1.5} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => onNavChange(activeNav === 'library' ? null : 'library')}
        >
          <Library className="h-4 w-4" strokeWidth={1.5} />
        </Button>

        <div className="flex-1" />
        <UsageMeter collapsed />
      </aside>
    )
  }

  return (
    <aside className="w-[220px] shrink-0 bg-sidebar border-r border-border flex flex-col h-full">
      {/* Header: User Profile Dropdown + Collapse */}
      <div className="flex items-center justify-between px-3 pt-3 pb-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="text-xs bg-pharma-primary text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="text-[13px] font-medium text-foreground truncate max-w-[110px]">{shortName}</span>
              <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="start" className="w-56">
            <DropdownMenuLabel className="font-normal text-xs text-muted-foreground truncate">
              {email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onOpenInvite}>
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Members
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onOpenSettings}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={onLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground"
          onClick={onToggleCollapse}
        >
          <ChevronsLeft className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      </div>

      {/* Workspace Switcher */}
      <div className="px-2 py-1">
        <WorkspaceSwitcher
          workspaces={workspaces}
          currentWorkspaceId={currentWorkspaceId}
          onWorkspaceChange={onWorkspaceChange}
          onCreateWorkspace={onCreateWorkspace}
          onOpenWorkspaceSettings={onOpenWorkspaceSettings}
        />
      </div>

      {/* New Button */}
      <div className="px-2 py-1">
        <Tooltip open={newButtonDisabled ? undefined : false}>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant="ghost"
                size="sm"
                className={`w-full justify-start gap-2 h-8 text-[13px] font-medium text-pharma-primary hover:bg-pharma-primary/5${newButtonDisabled ? ' opacity-50' : ''}`}
                onClick={onNewDocument}
                disabled={newButtonDisabled}
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                New
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{newButtonTooltip}</TooltipContent>
        </Tooltip>
      </div>

      {/* Navigation Items */}
      <nav className="flex flex-col gap-0.5 px-2 py-2">
        <Button
          variant="ghost"
          size="sm"
          className={`relative w-full justify-start gap-2 h-8 text-[13px] font-medium ${
            activeNav === 'documents'
              ? 'bg-pharma-primary-light text-foreground before:content-[""] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:bg-pharma-primary before:rounded-r'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => onNavChange(activeNav === 'documents' ? null : 'documents')}
        >
          <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
          Documents
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={`relative w-full justify-start gap-2 h-8 text-[13px] font-medium ${
            activeNav === 'library'
              ? 'bg-pharma-primary-light text-foreground before:content-[""] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:bg-pharma-primary before:rounded-r'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => onNavChange(activeNav === 'library' ? null : 'library')}
        >
          <Library className="h-3.5 w-3.5" strokeWidth={1.5} />
          Library
        </Button>
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Usage meter — checks used vs. monthly cap */}
      <UsageMeter collapsed={false} />
    </aside>
  )
}
